/**
 * Utilities for grouping raw annotations into semantic extraction entities
 */

import type { Annotation } from '../eventing/types'
import type {
  RawExtractionData,
  SemanticExtraction,
  Transaction,
  AccountInfo,
  StatementPeriod,
  ExtractedField
} from '../eventing/extraction-types'

/**
 * Group annotations by entity_key to link them to extracted fields
 */
function groupAnnotationsByEntityKey(annotations: Annotation[]): Map<string, Annotation[]> {
  const groups = new Map<string, Annotation[]>()
  
  for (const annotation of annotations) {
    // Get entity_key from the first link (annotations are linked to fields)
    const entityKey = annotation.links?.[0]?.entity_key
    if (!entityKey) continue
    
    const existing = groups.get(entityKey) || []
    groups.set(entityKey, [...existing, annotation])
  }
  
  return groups
}

/**
 * Calculate average confidence from annotations
 */
function calculateConfidence(annotations: Annotation[]): number {
  if (!annotations.length) return 0
  
  const sum = annotations.reduce((acc, ann) => {
    return acc + (ann.ai?.confidence || 0)
  }, 0)
  
  return sum / annotations.length
}

/**
 * Create an extracted field with linked annotations
 */
function createExtractedField(
  label: string,
  value: string | number | null | undefined,
  entityKey: string,
  annotationGroups: Map<string, Annotation[]>
): ExtractedField {
  const annotations = annotationGroups.get(entityKey) || []
  const confidence = calculateConfidence(annotations)
  
  // Get original OCR text from first annotation
  const originalValue = annotations[0]?.ocr?.text || annotations[0]?.text
  
  return {
    id: entityKey,
    label,
    value: value || null,
    original_value: originalValue,
    confidence,
    annotations,
    status: 'pending'
  }
}

/**
 * Build transaction from raw data and annotations
 */
function buildTransaction(
  transactionData: { date?: string; description?: string; amount?: number },
  index: number,
  annotationGroups: Map<string, Annotation[]>
): Transaction {
  const entityKey = `transaction_${index}`
  
  const date = createExtractedField(
    'Date',
    transactionData.date,
    `${entityKey}_date`,
    annotationGroups
  )
  
  const description = createExtractedField(
    'Description',
    transactionData.description,
    `${entityKey}_description`,
    annotationGroups
  )
  
  const amount = createExtractedField(
    'Amount',
    transactionData.amount,
    `${entityKey}_amount`,
    annotationGroups
  )
  
  const overallConfidence = (date.confidence + description.confidence + amount.confidence) / 3
  
  return {
    index,
    entity_key: entityKey,
    date,
    description,
    amount,
    overall_confidence: overallConfidence,
    is_collapsed: true,  // Start collapsed
    status: 'pending'
  }
}

/**
 * Build account info from raw data and annotations
 */
function buildAccountInfo(
  accountData: { account_holder_name?: string; account_number?: string; bank_name?: string },
  annotationGroups: Map<string, Annotation[]>
): AccountInfo {
  const entityKey = 'account_info'
  
  const accountHolderName = createExtractedField(
    'Account Holder',
    accountData.account_holder_name,
    `${entityKey}_account_holder_name`,
    annotationGroups
  )
  
  const accountNumber = createExtractedField(
    'Account Number',
    accountData.account_number,
    `${entityKey}_account_number`,
    annotationGroups
  )
  
  const bankName = createExtractedField(
    'Bank Name',
    accountData.bank_name,
    `${entityKey}_bank_name`,
    annotationGroups
  )
  
  const overallConfidence = (
    accountHolderName.confidence +
    accountNumber.confidence +
    bankName.confidence
  ) / 3
  
  return {
    entity_key: entityKey,
    account_holder_name: accountHolderName,
    account_number: accountNumber,
    bank_name: bankName,
    overall_confidence: overallConfidence,
    is_collapsed: false,  // Keep expanded by default
    status: 'pending'
  }
}

/**
 * Build statement period from raw data and annotations
 */
function buildStatementPeriod(
  periodData: {
    start_date?: string
    end_date?: string
    beginning_balance?: number
    ending_balance?: number
    total_deposits?: number
    total_withdrawals?: number
  },
  annotationGroups: Map<string, Annotation[]>
): StatementPeriod {
  const entityKey = 'statement_period'
  
  const startDate = createExtractedField(
    'Start Date',
    periodData.start_date,
    `${entityKey}_start_date`,
    annotationGroups
  )
  
  const endDate = createExtractedField(
    'End Date',
    periodData.end_date,
    `${entityKey}_end_date`,
    annotationGroups
  )
  
  const beginningBalance = createExtractedField(
    'Beginning Balance',
    periodData.beginning_balance,
    `${entityKey}_beginning_balance`,
    annotationGroups
  )
  
  const endingBalance = createExtractedField(
    'Ending Balance',
    periodData.ending_balance,
    `${entityKey}_ending_balance`,
    annotationGroups
  )
  
  const totalDeposits = createExtractedField(
    'Total Deposits',
    periodData.total_deposits,
    `${entityKey}_total_deposits`,
    annotationGroups
  )
  
  const totalWithdrawals = createExtractedField(
    'Total Withdrawals',
    periodData.total_withdrawals,
    `${entityKey}_total_withdrawals`,
    annotationGroups
  )
  
  const overallConfidence = (
    startDate.confidence +
    endDate.confidence +
    beginningBalance.confidence +
    endingBalance.confidence +
    totalDeposits.confidence +
    totalWithdrawals.confidence
  ) / 6
  
  return {
    entity_key: entityKey,
    start_date: startDate,
    end_date: endDate,
    beginning_balance: beginningBalance,
    ending_balance: endingBalance,
    total_deposits: totalDeposits,
    total_withdrawals: totalWithdrawals,
    overall_confidence: overallConfidence,
    is_collapsed: false,  // Keep expanded by default
    status: 'pending'
  }
}

/**
 * Main function: Build semantic extraction from raw data and annotations
 */
export function buildSemanticExtraction(
  workId: string,
  documentId: string,
  rawData: RawExtractionData,
  annotations: Annotation[]
): SemanticExtraction {
  console.log('buildSemanticExtraction: Starting', {
    workId,
    documentId,
    rawDataKeys: Object.keys(rawData),
    annotationCount: annotations.length
  })
  
  // Group annotations by entity_key
  const annotationGroups = groupAnnotationsByEntityKey(annotations)
  console.log('buildSemanticExtraction: Grouped annotations', {
    groupCount: annotationGroups.size,
    keys: Array.from(annotationGroups.keys())
  })
  
  // Build semantic structures
  const semanticData: SemanticExtraction = {
    work_id: workId,
    document_id: documentId,
    transactions: [],
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString()
  }
  
  // Build account info
  if (rawData.account_info) {
    semanticData.account_info = buildAccountInfo(rawData.account_info, annotationGroups)
    console.log('buildSemanticExtraction: Built account info', {
      confidence: semanticData.account_info.overall_confidence
    })
  }
  
  // Build statement period
  if (rawData.statement_period) {
    semanticData.statement_period = buildStatementPeriod(rawData.statement_period, annotationGroups)
    console.log('buildSemanticExtraction: Built statement period', {
      confidence: semanticData.statement_period.overall_confidence
    })
  }
  
  // Build transactions
  if (rawData.transactions && Array.isArray(rawData.transactions)) {
    semanticData.transactions = rawData.transactions.map((txn, index) =>
      buildTransaction(txn, index, annotationGroups)
    )
    console.log('buildSemanticExtraction: Built transactions', {
      count: semanticData.transactions.length
    })
  }
  
  return semanticData
}

/**
 * Get all annotations for a semantic entity (for highlighting)
 */
export function getAnnotationsForEntity(
  entity: Transaction | AccountInfo | StatementPeriod
): Annotation[] {
  const allAnnotations: Annotation[] = []
  
  // Collect annotations from all fields
  const fields = Object.values(entity).filter(
    (value): value is ExtractedField => 
      typeof value === 'object' && 
      value !== null && 
      'annotations' in value
  )
  
  for (const field of fields) {
    allAnnotations.push(...field.annotations)
  }
  
  return allAnnotations
}

